const YME_MAKE_LABELS = {
  ford: 'Ford',
  gm: 'GM',
  ram: 'Ram',
  jeep: 'Jeep',
  benz: 'Mercedes Benz',
  nissan: 'Nissan',
};

const YME_BODY_LABELS = {
  all: 'All body styles',
  pickup: 'Pickup',
  CC: 'Cab & Chassis',
  van: 'Van',
};

function formatYmeLabel(value, labels) {
  return labels[value] || value;
}

function formatEngineDisplay(engine) {
  return window.TdVehicleStorage?.formatEngineDisplay(engine) || String(engine || '').replace(/-/g, '.');
}

function formatVehicleTitle(vehicle) {
  if (!vehicle) return '';

  const make = formatYmeLabel(vehicle.make, YME_MAKE_LABELS);
  const year = vehicle.year || '';
  const engine = formatEngineDisplay(vehicle.engine);

  return `${year} ${make} ${engine}`.trim();
}

function formatVehicleDescription(vehicle) {
  if (!vehicle) return '';

  const body = formatYmeLabel(vehicle.body, YME_BODY_LABELS);
  return `Showing parts that fit your ${formatVehicleTitle(vehicle)}${body && vehicle.body !== 'all' ? ` (${body})` : ''}.`;
}

function updateYmeResultsBanner() {
  const vehicle = window.TdVehicleStorage?.getSelectedVehicle() || null;
  const titleEl = document.querySelector('[data-yme-vehicle-title]');
  const descriptionEl = document.querySelector('[data-yme-vehicle-description]');
  const mobileTextEl = document.querySelector('[data-yme-mobile-vehicle-text]');
  const section = document.querySelector('.yme-results');

  if (!section) return;

  if (!vehicle) {
    section.classList.add('yme-results--no-vehicle');
    if (titleEl) titleEl.textContent = 'Select your vehicle';
    if (descriptionEl) {
      descriptionEl.textContent =
        'Choose your year, make, engine, and body style to see compatible parts.';
    }
    if (mobileTextEl) mobileTextEl.textContent = 'Select your vehicle';
    window.TdYmmSelection?.initAll();
    return;
  }

  section.classList.remove('yme-results--no-vehicle');
  const title = `Parts for your ${formatVehicleTitle(vehicle)}`;
  const description = formatVehicleDescription(vehicle);

  if (titleEl) titleEl.textContent = title;
  if (descriptionEl) descriptionEl.textContent = description;
  if (mobileTextEl) mobileTextEl.textContent = formatVehicleTitle(vehicle);
}

class YmeResultsPage {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('#product-grid');
    this.collectionHandle = this.grid?.dataset.collectionHandle || '';
    this.collectionEl = root.querySelector('.collection');
    this.buildKitCardTemplate = root.querySelector('[data-yme-build-kit-card-template]');
    this.catalogItems = [];
    this.selectedProductTypes = [];
    this.isReady = false;
  }

  cancelScrollAnimations() {
    this.root.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
      element.classList.remove('scroll-trigger--offscreen');
    });
  }

  async init() {
    if (!this.grid || !this.collectionHandle) {
      this.setLoading(false);
      return;
    }

    this.cancelScrollAnimations();
    this.bindProductTypeBar();
    this.bindVehicleChanges();
    await this.refreshForSelectedVehicle();
  }

  bindVehicleChanges() {
    window.addEventListener('td:vehicle-storage-change', () => {
      this.refreshForSelectedVehicle();
    });

    window.addEventListener('storage', (event) => {
      if (event.key === window.TdVehicleStorage?.STORAGE_KEY) {
        this.refreshForSelectedVehicle();
      }
    });
  }

  async refreshForSelectedVehicle() {
    updateYmeResultsBanner();

    if (!this.grid || !this.collectionHandle) {
      this.setLoading(false);
      return;
    }

    const vehicle = window.TdVehicleStorage?.getSelectedVehicle() || null;
    if (!vehicle) {
      this.isReady = false;
      this.grid.innerHTML = '';
      this.setLoading(false);
      this.updateProductCount(0);
      return;
    }

    this.setLoading(true);

    try {
      if (!this.catalogItems.length) {
        this.catalogItems = await this.fetchCatalog();
      }

      this.isReady = true;
      this.applyFilters();
    } catch {
      this.renderMessage('Unable to load products right now. Please refresh and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  bindProductTypeBar() {
    const bar = this.root.querySelector('td-collection-product-type-bar');
    if (!bar) return;

    bar.addEventListener('click', (event) => {
      const button = event.target.closest('[data-product-type-button]');
      if (!button || button.disabled) return;

      bar.querySelectorAll('[data-product-type-button]').forEach((btn) => {
        btn.classList.toggle('is-active', btn === button);
      });

      const isAll = button.hasAttribute('data-product-type-action');
      this.selectedProductTypes = isAll
        ? []
        : (button.dataset.productTypeValues || '').split(',').map((value) => value.trim()).filter(Boolean);

      this.applyFilters();
    });
  }

  async fetchCatalog() {
    const items = [];
    let page = 1;
    const pageSize = 50;

    while (page) {
      const response = await fetch(`/collections/${this.collectionHandle}?view=yme-grid&page=${page}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const pageItems = [...doc.querySelectorAll('[data-yme-product]')];

      if (pageItems.length === 0) break;

      pageItems.forEach((element) => {
        items.push(element);
      });

      const nextPageValue = doc.querySelector('[data-yme-next-page]')?.dataset.ymeNextPage;
      const nextPage = Number(nextPageValue);

      if (nextPage > page) {
        page = nextPage;
        continue;
      }

      if (pageItems.length < pageSize) break;

      page += 1;
    }

    return items;
  }

  applyFilters() {
    if (!this.isReady) return;

    const vehicle = window.TdVehicleStorage?.getSelectedVehicle() || null;
    const { productMatchesVehicle, productMatchesProductTypes, sortByProductType } = window.TdYmeFitment;

    if (!vehicle) {
      this.updateProductCount(0);
      return;
    }

    const vehicleMatches = this.catalogItems.filter((element) => productMatchesVehicle(element, vehicle));
    const filtered = sortByProductType(
      vehicleMatches.filter((element) => productMatchesProductTypes(element, this.selectedProductTypes))
    );

    this.renderProducts(filtered);
    this.updateProductCount(filtered.length);
  }

  shouldShowBuildKitCard() {
    if (!this.selectedProductTypes.length) return true;

    return this.selectedProductTypes.some((type) => type.toLowerCase() === 'all-in-one kit');
  }

  prependBuildKitCard() {
    const template = this.buildKitCardTemplate;
    const card = template?.content?.firstElementChild?.cloneNode(true);
    if (!card || !this.grid) return;

    const titleEl = card.querySelector('[data-yme-build-kit-title]');
    const vehicleLabel = formatVehicleTitle(window.TdVehicleStorage?.getSelectedVehicle());
    if (titleEl) {
      titleEl.textContent = vehicleLabel
        ? `Build a Custom Kit for your ${vehicleLabel}`
        : 'Build a Custom Kit';
    }

    card.style.setProperty('--animation-order', '1');
    card.classList.add('scroll-trigger--cancel');
    card.classList.remove('scroll-trigger--offscreen');
    this.grid.prepend(card);
  }

  appendEmptyMessage(message) {
    this.root.querySelector('.yme-results__empty')?.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'title-wrapper center yme-results__empty';
    wrapper.innerHTML = `<h2 class="title title--primary">${message}</h2>`;
    this.grid.appendChild(wrapper);
  }

  renderProducts(elements) {
    this.grid.innerHTML = '';
    this.root.querySelector('.yme-results__empty')?.remove();
    this.collectionEl?.classList.toggle('collection--empty', elements.length === 0);

    const showBuildKitCard = this.shouldShowBuildKitCard();
    if (showBuildKitCard) this.prependBuildKitCard();

    if (elements.length === 0) {
      this.appendEmptyMessage('No products match your vehicle and filters. Try a different product type.');
      return;
    }

    const animationOffset = showBuildKitCard ? 2 : 1;

    elements.forEach((element, index) => {
      const item = element.cloneNode(true);
      item.style.removeProperty('--animation-order');
      item.style.setProperty('--animation-order', String(index + animationOffset));
      item.classList.add('scroll-trigger--cancel');
      item.classList.remove('scroll-trigger--offscreen');
      this.grid.appendChild(item);
    });
  }

  renderMessage(message) {
    this.grid.innerHTML = '';
    this.collectionEl?.classList.add('collection--empty');
    this.appendEmptyMessage(message);
  }

  updateProductCount(count) {
    const countText = count === 1 ? '1 product' : `${count} products`;
    const countEl = document.getElementById('YmeProductCount');
    if (countEl) countEl.textContent = countText;
  }

  setLoading(isLoading) {
    this.collectionEl?.classList.toggle('loading', isLoading);
    this.grid?.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    const countEl = document.getElementById('YmeProductCount');
    if (countEl && isLoading) {
      countEl.textContent = 'Loading products…';
    }
  }

  serializeProduct(element) {
    const price = Number(element.dataset.price || 0);
    const link = element.querySelector('a[href]');

    return {
      title: element.dataset.productTitle || '',
      type: element.dataset.productType || '',
      vendor: element.dataset.vendor || '',
      price,
      available: element.dataset.available === 'true',
      url: link?.href || null,
    };
  }

  resolveProductTypes(productType) {
    const wanted = String(productType || '').trim();
    if (!wanted) return [];

    const known = [
      ...new Set(
        this.catalogItems
          .map((element) => (element.dataset.productType || '').trim())
          .filter(Boolean)
      ),
    ];
    const lower = wanted.toLowerCase();
    const exact = known.filter((type) => type.toLowerCase() === lower);
    if (exact.length) return exact;

    const partial = known.filter((type) => type.toLowerCase().includes(lower));
    return partial.length ? partial : [wanted];
  }

  getMatchingProducts({ productType, query, limit } = {}) {
    const vehicle = window.TdVehicleStorage?.getSelectedVehicle() || null;
    if (!vehicle?.make) {
      return {
        error: 'No vehicle selected. Use set_shopper_vehicle first.',
        selected: false,
      };
    }

    const fitment = window.TdYmeFitment;
    if (!fitment?.productMatchesVehicle) {
      return { error: 'Fitment matching is not available on this page.' };
    }

    if (!this.isReady) {
      return {
        error: 'Catalog still loading. Retry search_parts_for_vehicle shortly.',
        loading: true,
        vehicle: {
          year: vehicle.year,
          make: vehicle.make,
          engine: vehicle.engine,
          label: formatVehicleTitle(vehicle),
        },
      };
    }

    const typeFilters = this.resolveProductTypes(productType);
    let matches = this.catalogItems.filter((element) => fitment.productMatchesVehicle(element, vehicle));

    if (typeFilters.length) {
      matches = matches.filter((element) => fitment.productMatchesProductTypes(element, typeFilters));
    }

    matches = fitment.sortByProductType(matches);

    const queryText = String(query || '').trim().toLowerCase();
    if (queryText) {
      matches = matches.filter((element) => {
        const haystack = [
          element.dataset.productTitle,
          element.dataset.productType,
          element.dataset.vendor,
          element.dataset.tags,
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(queryText);
      });
    }

    const max = Math.min(Math.max(Number(limit) || 12, 1), 50);

    return {
      vehicle: {
        year: vehicle.year,
        make: vehicle.make,
        engine: vehicle.engine,
        body: vehicle.body || 'all',
        label: formatVehicleTitle(vehicle),
      },
      productType: typeFilters[0] || null,
      query: queryText || null,
      count: matches.length,
      returned: Math.min(matches.length, max),
      bundleBuilderUrl: '/pages/bundle-builder',
      message: 'These parts fit the selected truck. If the shopper wants a full custom kit, send them to bundleBuilderUrl instead of adding unrelated SKUs.',
      products: matches.slice(0, max).map((element) => this.serializeProduct(element)),
    };
  }
}

function vehiclesAreSame(a, b) {
  return ['make', 'year', 'engine', 'body'].every(
    (key) => String(a?.[key] || 'all') === String(b?.[key] || 'all')
  );
}

// A vehicle in the URL wins over storage: it is what the shopper just searched for (or a
// shared link), and it is the only source when the browser blocks localStorage.
function applyVehicleFromUrl() {
  const storage = window.TdVehicleStorage;
  const urlVehicle = storage?.vehicleFromSearchParams(window.location.search);
  if (!urlVehicle || vehiclesAreSame(urlVehicle, storage.getSelectedVehicle())) return;

  storage.addVehicle(urlVehicle);
}

// Keep the address bar on the current vehicle so refreshes and shared links stay correct.
function syncUrlWithSelectedVehicle() {
  const storage = window.TdVehicleStorage;
  if (!storage) return;

  // Swap only the vehicle params so utm_* and other tracking params survive.
  const params = new URLSearchParams(window.location.search);
  ['make', 'year', 'engine', 'body'].forEach((key) => params.delete(key));
  const vehicleParams = new URL(storage.getResultsUrl(), window.location.origin).searchParams;
  vehicleParams.forEach((value, key) => params.set(key, value));

  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  if (nextUrl === window.location.pathname + window.location.search + window.location.hash) return;

  window.history.replaceState(window.history.state, '', nextUrl);
}

const ymeResultsRoot = document.querySelector('.yme-results');
if (ymeResultsRoot) {
  applyVehicleFromUrl();
  syncUrlWithSelectedVehicle();
  window.addEventListener('td:vehicle-storage-change', syncUrlWithSelectedVehicle);

  const ymeResultsPage = new YmeResultsPage(ymeResultsRoot);
  window.TdYmeResults = ymeResultsPage;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ymeResultsPage.init());
  } else {
    ymeResultsPage.init();
  }
}
